import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
    CreditCard, Crown, AlertTriangle, CheckCircle2, ArrowUpCircle, ArrowDownCircle, XCircle, RefreshCw,
} from "lucide-react";

const EYEBROW = "font-eyebrow text-[11px] font-semibold uppercase tracking-[0.18em] sm:text-xs";
const MAROON = "#4A0E17";
const GOLD = "#C9A227";

const inr = (n) => `₹${(n || 0).toLocaleString("en-IN")}`;

const navBtn = "border-[#E8E4DE] bg-white text-[#3D2817]";

const STATUS_BADGE = {
    trial: { bg: "#FDF3DC", text: "#9A7B10", label: "Trial" },
    active: { bg: "#DCEEDC", text: "#1F7A55", label: "Active" },
    past_due: { bg: "#FEF5F3", text: "#C84B31", label: "Past due" },
    cancelled: { bg: "#F3F0EB", text: "#8A8178", label: "Cancelled" },
    expired: { bg: "#F3F0EB", text: "#8A8178", label: "Expired" },
};

function formatDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
    } catch {
        return "—";
    }
}

function SalonCard({ children, className = "" }) {
    return (
        <div className={`relative overflow-hidden rounded-xl border border-[#E8E4DE] bg-white shadow-[0_4px_22px_rgba(45,41,38,0.04)] ${className}`}>
            <div className="absolute inset-x-0 top-0 h-[2px] bg-[#4A0E17]/85" />
            {children}
        </div>
    );
}

export default function Billing() {
    const location = useLocation();
    const [status, setStatus] = useState(null);
    const [plans, setPlans] = useState([]);
    const [history, setHistory] = useState([]);
    const [cycle, setCycle] = useState("monthly");
    const [busyPlanId, setBusyPlanId] = useState(null);
    const [headerBusy, setHeaderBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const [s, p, h] = await Promise.all([
                api.get("/billing/status"),
                api.get("/billing/plans"),
                api.get("/billing/history"),
            ]);
            setStatus(s.data);
            setPlans(p.data || []);
            setHistory(h.data || []);
        } catch {
            toast.error("Could not load billing");
        }
    }, []);

    useEffect(() => {
        load();
        api.post("/billing/sync").then((r) => setStatus(r.data)).catch(() => {});
    }, [load]);

    useEffect(() => {
        if (status?.billing_cycle) setCycle(status.billing_cycle);
    }, [status?.billing_cycle]);

    useEffect(() => {
        const st = location.state;
        if (!st?.fromRegister) return;
        if (st.cycle === "yearly" || st.cycle === "monthly") setCycle(st.cycle);
        toast.message("Your free trial has started. Subscribe anytime below to activate billing.");
        window.history.replaceState({}, document.title);
    }, [location.state]);

    const openSubscriptionCheckout = (data) => new Promise((resolve) => {
        if (!data?.key_id) {
            resolve(false);
            return;
        }
        if (!window.Razorpay) {
            toast.error("Razorpay checkout failed to load. Refresh the page and try again.");
            resolve(false);
            return;
        }

        const isOrder = data.checkout_mode === "order" && data.order_id;
        const isSub = data.subscription_id && !isOrder;
        if (!isOrder && !isSub) {
            resolve(false);
            return;
        }

        const options = {
            key: data.key_id,
            name: "WebSalon",
            description: `${data.plan_name || "Plan"} (${data.billing_cycle || cycle})`,
            prefill: data.prefill || {},
            theme: { color: MAROON },
            handler: async (res) => {
                try {
                    const vr = await api.post("/billing/verify", {
                        razorpay_payment_id: res.razorpay_payment_id,
                        razorpay_signature: res.razorpay_signature,
                        razorpay_subscription_id: res.razorpay_subscription_id,
                        razorpay_order_id: res.razorpay_order_id || data.order_id,
                    });
                    setStatus(vr.data.status || vr.data);
                    toast.success("Payment successful — subscription activated");
                    load();
                } catch {
                    toast.error("Payment verification failed");
                } finally {
                    resolve(true);
                }
            },
            modal: { ondismiss: () => resolve(false) },
        };

        if (isOrder) {
            options.order_id = data.order_id;
            options.amount = data.amount;
            options.currency = data.currency || "INR";
        } else {
            options.subscription_id = data.subscription_id;
        }

        const rzp = new window.Razorpay(options);
        rzp.on("payment.failed", (resp) => {
            const msg = resp?.error?.description || "";
            if (/recurring/i.test(msg)) {
                toast.error(
                    "Recurring payments are not enabled on this Razorpay account. Ask the platform admin to set BILLING_CHECKOUT_MODE=order in backend .env and restart.",
                    { duration: 8000 },
                );
            } else {
                toast.error(msg || "Payment failed");
            }
        });
        rzp.open();
    });

    const subscribe = async (planId) => {
        setBusyPlanId(planId);
        try {
            const r = await api.post("/billing/subscribe", { plan_id: planId, billing_cycle: cycle });
            if (r.data.subscription_id && r.data.key_id) {
                await openSubscriptionCheckout(r.data);
                return;
            }
            if (r.data.order_id && r.data.key_id) {
                await openSubscriptionCheckout(r.data);
                return;
            }
            setStatus(r.data.status || r.data);
            if (r.data.simulated) {
                toast.success("Plan activated (offline mode — no payment taken)");
            }
            load();
        } catch (err) {
            const d = err?.response?.data?.detail;
            const msg = typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x?.msg || JSON.stringify(x)).join(", ") : "Subscribe failed";
            toast.error(msg);
        } finally {
            setBusyPlanId(null);
        }
    };

    const changePlan = async (planId, isUpgrade) => {
        setBusyPlanId(planId);
        try {
            const r = await api.post("/billing/change-plan", {
                plan_id: planId,
                schedule: isUpgrade ? "now" : "cycle_end",
                billing_cycle: cycle,
            });
            if (r.data.subscription_id && r.data.key_id) {
                await openSubscriptionCheckout(r.data);
                return;
            }
            if (r.data.order_id && r.data.key_id) {
                await openSubscriptionCheckout(r.data);
                return;
            }
            setStatus(r.data.status);
            toast.success(
                r.data.simulated
                    ? "Plan updated (offline mode)"
                    : isUpgrade
                        ? "Plan upgraded"
                        : "Downgrade scheduled for end of billing period",
            );
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Plan change failed");
        } finally {
            setBusyPlanId(null);
        }
    };

    const cancelSub = async () => {
        if (!window.confirm("Cancel subscription at end of current billing period?")) return;
        setHeaderBusy(true);
        try {
            const r = await api.post("/billing/cancel", { at_period_end: true });
            setStatus(r.data.status);
            toast.success("Cancellation scheduled");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Cancel failed");
        } finally {
            setHeaderBusy(false);
        }
    };

    const resumeSub = async () => {
        setHeaderBusy(true);
        try {
            const r = await api.post("/billing/resume");
            setStatus(r.data.status);
            toast.success("Subscription resumed");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Resume failed");
        } finally {
            setHeaderBusy(false);
        }
    };

    if (!status) {
        return (
            <div className="flex min-h-[40vh] items-center justify-center p-6">
                <p className="font-serif text-xl text-[#8A8178]">Loading subscription…</p>
            </div>
        );
    }

    const sub = status.subscription_status || "trial";
    const isPaid = !!status.is_paid_subscriber;
    const isTrial = !isPaid;
    const badge = isPaid
        ? (STATUS_BADGE[sub] || STATUS_BADGE.active)
        : STATUS_BADGE.trial;
    const currentPlanId = status.plan?.id;
    const planPriceForCycle = (p, c) => (c === "yearly" ? p.price_yearly : p.price_monthly) || 0;
    const planPrice = (p) => planPriceForCycle(p, cycle);
    const paidCycle = status.billing_cycle || "monthly";
    const currentPrice = isPaid ? planPriceForCycle(status.plan || {}, paidCycle) : 0;

    const planAction = (p) => {
        const price = isPaid ? planPriceForCycle(p, paidCycle) : planPrice(p);
        const isTrialPlan = isTrial && p.id === currentPlanId;
        const isPaidCurrent = isPaid && p.id === currentPlanId;
        const isUpgrade = isPaid && price > currentPrice;
        const isDowngrade = isPaid && price < currentPrice;
        const planBusy = busyPlanId === p.id;
        const anyBusy = !!busyPlanId;

        if (isPaidCurrent) {
            return (
                <Button type="button" disabled className="h-10 w-full" variant="outline">
                    Current plan
                </Button>
            );
        }

        if (!isPaid) {
            return (
                <Button
                    type="button"
                    disabled={anyBusy}
                    className="h-10 w-full bg-[#4A0E17] text-white hover:bg-[#3a0b12]"
                    onClick={() => subscribe(p.id)}
                >
                    {planBusy ? "Opening checkout…" : isTrialPlan ? "Subscribe to this plan" : "Subscribe"}
                </Button>
            );
        }

        if (isUpgrade) {
            return (
                <Button
                    type="button"
                    disabled={anyBusy}
                    className="h-10 w-full bg-[#4A0E17] text-white hover:bg-[#3a0b12]"
                    onClick={() => changePlan(p.id, true)}
                >
                    <ArrowUpCircle className="mr-1.5 size-4" />
                    {planBusy ? "Processing…" : "Upgrade now"}
                </Button>
            );
        }

        if (isDowngrade) {
            return (
                <Button
                    type="button"
                    disabled={anyBusy}
                    variant="outline"
                    className="h-10 w-full"
                    onClick={() => {
                        if (!window.confirm(
                            `Downgrade to ${p.name} at your next renewal${status.current_period_end ? ` (${formatDate(status.current_period_end)})` : ""}? You keep your current plan until then.`,
                        )) return;
                        changePlan(p.id, false);
                    }}
                >
                    <ArrowDownCircle className="mr-1.5 size-4" />
                    {planBusy ? "Processing…" : "Downgrade at renewal"}
                </Button>
            );
        }

        return (
            <Button type="button" disabled className="h-10 w-full" variant="outline">
                Same price tier
            </Button>
        );
    };

    return (
        <div className="space-y-6 p-6 lg:p-8" data-testid="salon-billing-page">
            <header>
                <p className={EYEBROW} style={{ color: GOLD }}>Subscription</p>
                <h1 className="mt-1 font-serif text-3xl font-semibold text-[#4A0E17]">Subscription &amp; plans</h1>
                <p className="mt-1 text-sm text-[#8A8178]">Manage your WebSalon platform subscription</p>
            </header>

            {status.checkout_mode === "subscription" && (
                <SalonCard className="border-[#E8E4DE] bg-[#FAFAF8] p-4">
                    <p className="text-sm text-[#3D2817]">
                        <strong>Recurring checkout:</strong> test with card <code className="rounded bg-white px-1 text-xs">5267 3181 8797 5449</code> (Mastercard).
                        Card <code className="rounded bg-white px-1 text-xs">4111…</code> does not support subscriptions.
                    </p>
                </SalonCard>
            )}

            {isTrial && status.plan?.name && (
                <SalonCard className="border-[#FDF3DC] bg-[#FFFBF5] p-4">
                    <p className="flex items-start gap-2 text-sm text-[#9A7B10]">
                        <AlertTriangle className="mt-0.5 size-4 shrink-0" />
                        You&apos;re on a <strong>free trial</strong> of {status.plan.name}. No payment has been taken.
                        {status.trial_status === "active" && (
                            <> Trial ends {formatDate(status.trial_ends_at)}.</>
                        )}
                        {" "}Subscribe below to continue after your trial.
                    </p>
                </SalonCard>
            )}

            {status.pending_checkout && (
                <SalonCard className="border-[#FDE8EC] bg-[#FFFBFB] p-4">
                    <p className="flex items-start gap-2 text-sm text-[#C84B31]">
                        <AlertTriangle className="mt-0.5 size-4 shrink-0" />
                        Payment not completed. Choose a plan and subscribe to finish checkout.
                    </p>
                </SalonCard>
            )}

            {status.is_simulated_subscription && status.razorpay_configured && (
                <SalonCard className="border-[#FDF3DC] bg-[#FFFBF5] p-4">
                    <p className="flex items-start gap-2 text-sm text-[#9A7B10]">
                        <AlertTriangle className="mt-0.5 size-4 shrink-0" />
                        Your plan is active without a Razorpay payment on file. Choose a plan below to open secure checkout and complete payment.
                    </p>
                </SalonCard>
            )}

            <SalonCard className="p-5">
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                        <div className="flex items-center gap-2">
                            <CreditCard className="size-5 text-[#C9A227]" />
                            <span
                                className="rounded-full px-2.5 py-0.5 text-xs font-bold uppercase"
                                style={{ background: badge.bg, color: badge.text }}
                            >
                                {badge.label}
                            </span>
                        </div>
                        <p className="mt-3 font-serif text-2xl text-[#4A0E17]">
                            {status.plan?.name || "No plan selected"}
                            {isTrial && status.plan?.name && (
                                <span className="ml-2 text-sm font-medium text-[#9A7B10]">(trial)</span>
                            )}
                        </p>
                        {isPaid && status.billing_cycle && (
                            <p className="mt-1 text-sm text-[#8A8178] capitalize">
                                Billed {status.billing_cycle}
                            </p>
                        )}
                        {isTrial && status.trial_status === "active" && (
                            <p className="mt-1 flex items-center gap-1.5 text-sm text-[#9A7B10]">
                                <AlertTriangle className="size-4" />
                                Trial ends {formatDate(status.trial_ends_at)} · not billed yet
                            </p>
                        )}
                        {isPaid && sub === "active" && status.current_period_end && (
                            <p className="mt-1 text-sm text-[#3D2817]">
                                Renews on {formatDate(status.current_period_end)}
                            </p>
                        )}
                        {status.cancel_at_period_end && sub === "active" && (
                            <p className="mt-2 text-sm font-medium text-[#C84B31]">
                                Cancels at end of billing period — use Resume to keep your plan
                            </p>
                        )}
                        {status.cancel_at_period_end && sub !== "active" && (
                            <p className="mt-2 text-sm font-medium text-[#C84B31]">
                                Subscription signup was cancelled — use Resume to subscribe again
                            </p>
                        )}
                        {status.scheduled_plan && (
                            <p className="mt-2 text-sm text-[#3D2817]">
                                Downgrade to <strong>{status.scheduled_plan.name}</strong> scheduled
                            </p>
                        )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Button type="button" variant="outline" size="sm" className={navBtn} disabled={headerBusy || !!busyPlanId} onClick={load}>
                            <RefreshCw className="mr-1 size-3.5" /> Refresh
                        </Button>
                        {status.cancel_at_period_end ? (
                            <Button type="button" size="sm" className="bg-[#4A0E17] text-white" disabled={headerBusy || !!busyPlanId} onClick={resumeSub} title="Undo scheduled cancellation">
                                Resume subscription
                            </Button>
                        ) : isPaid && !status.is_simulated_subscription ? (
                            <Button type="button" variant="outline" size="sm" disabled={headerBusy || !!busyPlanId} onClick={cancelSub}>
                                <XCircle className="mr-1 size-3.5" /> Cancel
                            </Button>
                        ) : null}
                    </div>
                </div>
            </SalonCard>

            <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm font-medium text-[#3D2817]">Billing cycle:</span>
                <Button
                    type="button"
                    size="sm"
                    variant={cycle === "monthly" ? "default" : "outline"}
                    className={cycle === "monthly" ? "bg-[#4A0E17] text-white" : ""}
                    disabled={!!busyPlanId || (isPaid && paidCycle !== "monthly")}
                    onClick={() => setCycle("monthly")}
                >
                    Monthly
                </Button>
                <Button
                    type="button"
                    size="sm"
                    variant={cycle === "yearly" ? "default" : "outline"}
                    className={cycle === "yearly" ? "bg-[#4A0E17] text-white" : ""}
                    disabled={!!busyPlanId || (isPaid && paidCycle !== "yearly")}
                    onClick={() => setCycle("yearly")}
                >
                    Yearly
                </Button>
                {isPaid && (
                    <span className="text-xs text-[#8A8178]">Cycle changes apply on upgrade via checkout</span>
                )}
                {!isPaid && cycle === "yearly" && (
                    <span className="text-xs font-medium text-[#2D9B6E]">Save vs paying monthly</span>
                )}
            </div>

            <div className="grid items-stretch gap-4 md:grid-cols-2 lg:grid-cols-3">
                {plans.map((p) => {
                    const price = planPrice(p);
                    const featured = p.highlighted;
                    const isTrialPlan = isTrial && p.id === currentPlanId;

                    return (
                        <SalonCard
                            key={p.id}
                            className={`flex h-full flex-col p-5 ${
                                featured ? "ring-2 ring-[#D4AF37]/50" : ""
                            } ${isTrialPlan ? "ring-2 ring-[#D4AF37]/30" : ""}`}
                        >
                            <div className="flex items-start justify-between gap-2">
                                <div className="flex items-center gap-2">
                                    <Crown className="size-4 shrink-0 text-[#C9A227]" />
                                    <h3 className="font-serif text-xl text-[#4A0E17]">{p.name}</h3>
                                </div>
                                <div className="flex shrink-0 flex-col items-end gap-1">
                                    {featured && (
                                        <span className="rounded-full bg-[#FDF3DC] px-2 py-0.5 text-[10px] font-bold uppercase text-[#9A7B10]">
                                            Popular
                                        </span>
                                    )}
                                    {isTrialPlan && (
                                        <span className="rounded-full bg-[#FDF3DC] px-2 py-0.5 text-[10px] font-bold uppercase text-[#9A7B10]">
                                            Your trial
                                        </span>
                                    )}
                                </div>
                            </div>
                            <p className="mt-1 min-h-[2.5rem] text-sm leading-snug text-[#8A8178]">{p.description}</p>
                            <p className="mt-3 font-serif text-3xl text-[#4A0E17]">
                                {inr(price)}
                                <span className="text-sm font-medium text-[#8A8178]">/{cycle === "yearly" ? "yr" : "mo"}</span>
                            </p>
                            <p className="mt-1 min-h-[1rem] text-xs text-[#8A8178]">
                                {cycle === "yearly" && p.price_monthly > 0 && p.price_yearly > 0
                                    ? `≈ ${inr(Math.round(p.price_yearly / 12))}/mo billed annually`
                                    : "\u00A0"}
                            </p>
                            <ul className="mt-4 flex-1 space-y-1.5 text-sm text-[#3D2817]">
                                {(p.features || []).slice(0, 5).map((f) => (
                                    <li key={f} className="flex items-start gap-2">
                                        <CheckCircle2 className="mt-0.5 size-3.5 shrink-0 text-[#2D9B6E]" />
                                        {f}
                                    </li>
                                ))}
                            </ul>
                            <p className="mt-3 text-xs text-[#8A8178]">
                                Up to {p.max_staff} staff · {p.max_outlets} outlet{p.max_outlets > 1 ? "s" : ""}
                            </p>
                            <div className="mt-auto pt-4">
                                {planAction(p)}
                            </div>
                        </SalonCard>
                    );
                })}
            </div>

            {(isPaid || history.length > 0) && (
            <SalonCard className="p-5">
                <p className={EYEBROW} style={{ color: GOLD }}>Payment history</p>
                <h2 className="mt-1 font-serif text-xl text-[#4A0E17]">Subscription payments</h2>
                <div className="mt-4 overflow-x-auto">
                    <table className="min-w-full border-collapse text-sm">
                        <thead>
                            <tr className="border-b border-[#E8E4DE] text-left text-[11px] font-bold uppercase tracking-wider text-[#8A8178]">
                                <th className="py-2 pr-4">Date</th>
                                <th className="py-2 pr-4">Plan</th>
                                <th className="py-2 pr-4">Amount</th>
                                <th className="py-2 pr-4">Cycle</th>
                                <th className="py-2">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {history.map((r) => (
                                <tr key={r.id} className="border-b border-[#E8E4DE]/50">
                                    <td className="py-2.5 pr-4 text-[#8A8178]">{formatDate(r.paid_at || r.created_at)}</td>
                                    <td className="py-2.5 pr-4 font-medium text-[#3D2817]">{r.plan?.name || "—"}</td>
                                    <td className="py-2.5 pr-4 font-semibold text-[#4A0E17]">{inr(r.amount)}</td>
                                    <td className="py-2.5 pr-4 capitalize text-[#3D2817]">{r.billing_cycle}</td>
                                    <td className="py-2.5 capitalize text-[#3D2817]">{r.status}</td>
                                </tr>
                            ))}
                            {history.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="py-8 text-center text-[#8A8178]">
                                        No subscription payments yet
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </SalonCard>
            )}

            {isTrial && history.length === 0 && (
                <p className="text-center text-sm text-[#8A8178]">
                    Payment history appears here after your first subscription payment.
                </p>
            )}
        </div>
    );
}
